'use client';



import React, { useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/components/ThemeProvider';
import { ConvexClientProvider } from '@/providers/convex-client-provider';
import AuthProvider from '@/contexts/AuthContext';
import { MessagingProvider } from '@/contexts/MessagingContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { DeveloperBreadcrumbProvider } from '@/contexts/DeveloperBreadcrumbContext';
import AdminContentWrapper from '@/components/admin/AdminContentWrapper';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { iconService } from '@/services/icon.service';
import { isFeatureEnabled } from '@/lib/feature-flags';
import { initSentry } from '@/utils/sentry';

if (typeof window !== 'undefined') {
  initSentry();

  if (isFeatureEnabled('useIconifyLibrary')) {
    import('@/services/iconify-offline-loader');
  }
}

const ServiceWorkerManager = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') {
      return;
    }

    let intervalId: number | undefined;

    const registerServiceWorker = () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          intervalId = window.setInterval(() => {
            registration.update();
          }, 60000);
        })
        .catch((error) => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('SW registration failed:', error);
          }
        });
    };

    const onControllerChange = () => {
      window.location.reload();
    };

    if (document.readyState === 'complete') {
      registerServiceWorker();
    } else {
      window.addEventListener('load', registerServiceWorker, { once: true });
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      window.removeEventListener('load', registerServiceWorker);
    };
  }, []);

  return <>{children}</>;
};

const IconServiceInitializer = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    if (isFeatureEnabled('useIconifyLibrary')) {
      // Warm up the icon cache; stats logging is dev-only.
      const stats = iconService.getStats();
      if (process.env.NODE_ENV !== 'production') {
        console.log('Icon service initialized:', stats);
      }
    }
  }, []);

  return <>{children}</>;
};

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <ConvexClientProvider>
        <AuthProvider>
          <ThemeProvider defaultTheme="dark" storageKey="out-there-theme">
            <NotificationProvider>
              <MessagingProvider>
                <DeveloperBreadcrumbProvider>
                  <ServiceWorkerManager>
                    <IconServiceInitializer>
                      <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <AdminContentWrapper>
                          <NavigationProvider>
                            {children}
                          </NavigationProvider>
                        </AdminContentWrapper>
                      </TooltipProvider>
                    </IconServiceInitializer>
                  </ServiceWorkerManager>
                </DeveloperBreadcrumbProvider>
              </MessagingProvider>
            </NotificationProvider>
          </ThemeProvider>
        </AuthProvider>
      </ConvexClientProvider>
    </ErrorBoundary>
  );
}
